// Funnel v2 sales landing — NEW page at /blueprint (issue #266, fixes #255).
//
// The existing "/" early-access page stays live; ad traffic for the live
// funnel lands here. Long-form direct response in the operator register:
// problem → possibility → mechanism → fit → comparison → founder → offer →
// questions → close. Coral appears only on the purchase action.

import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import {
  BriefcaseBusiness,
  Check,
  Lock,
  ShieldCheck,
  X,
} from 'lucide-react';
import {
  BlueprintGrid,
  DisplayHeading,
  Kicker,
  LedgerCard,
  LedgerRow,
  PrimaryCtaLink,
  SectionMark,
  ValuePromise,
} from '../../components/marketing/funnel/atoms';
import { FunnelPriceNotice } from '../../components/marketing/funnel/FunnelPriceNotice';
import { formatUsd, useFunnelPriceCatalog } from '../../lib/funnel-prices';

function ctaLabel(cents: number): string {
  return `Get your Blueprint — ${formatUsd(cents)}`;
}

function Section({ id, children, className = '' }: { id?: string; children: ReactNode; className?: string }) {
  return (
    <section id={id} className={`border-t border-white/[0.07] px-5 py-16 md:px-8 md:py-24 ${className}`}>
      <div className="mx-auto max-w-5xl">{children}</div>
    </section>
  );
}

const costOptions = [
  ['Hire more people.', 'Recruiting, training, managing, payroll — more of the time you do not have.'],
  ['Hire a VA.', 'Writing SOPs you have not written, supervising work that should run itself.'],
  ['Buy more software.', 'Evaluating tools, integrating systems, paying monthly for 30% of one problem.'],
  ['Chat with AI.', 'Getting advice, copy-pasting, and doing the actual work yourself anyway.'],
];

const jobs = [
  ['Customer support', 'She reads every email, drafts routine replies, and flags the ones that need you.'],
  ['Sales follow-up', 'She follows up with every lead, books calls, and updates your CRM after each touch.'],
  ['Office management', 'She watches your inbox, calendar, and projects — then tells you what needs attention.'],
  ['Content writing', 'She drafts newsletters, social posts, and follow-up sequences in your voice.'],
  ['Reporting', 'She pulls your numbers, writes the summary, and emails it every Monday morning.'],
];

const audience = [
  ['Agencies', 'Turn client calls into project briefs. Draft campaigns from intake forms. Prepare reporting summaries. Track follow-ups and handoffs.'],
  ['Consultants', 'Summarize discovery calls. Draft proposals and scopes. Research client industries. Build reusable delivery templates.'],
  ['Coaches and course creators', 'Triage student questions. Draft weekly emails. Repurpose content. Generate promotional copy in your voice.'],
  ['Solo founders', 'Organize ideas into execution plans. Draft outreach. Research leads. Document repeatable processes.'],
  ['Operations-heavy businesses', 'Process intake requests. Summarize internal updates. Route tasks. Draft SOPs. Monitor recurring workflows.'],
];

const comparisons = [
  ['ChatGPT', 'One drafts emails you still have to send. The other answers them.'],
  ['Zapier', 'One moves data between tools. The other makes decisions and takes action.'],
  ['Your CRM', 'One is a database. The other is a sales assistant who never forgets to follow up.'],
  ['A virtual assistant', 'One costs $2,000+ a month and needs hiring, training, and managing. The other shows up already knowing your business.'],
];

const faqs = [
  ['Is this just ChatGPT in a different wrapper?', 'No. ChatGPT chats. RoleForge produces a specific plan for the jobs your AI employee should hold, what she should do, and how those jobs fit together. The output is "here is your AI workforce" — not "here is some text you can copy."'],
  ['What kind of business does this work for?', 'Service businesses between $1M and $10M in revenue: agencies, consultancies, professional services, coaches, creators, small B2B firms. Anywhere the work is mostly communication, coordination, follow-up, and content.'],
  ['Do I have to install anything?', 'No. RoleForge runs in your browser.'],
  ['What if I do not know which jobs to give her?', 'That is the entire point. The guided conversation learns your business and recommends the specific jobs she should hold for your specific situation. You do not bring expertise — you bring your business.'],
  ['How long does the Blueprint take?', 'As long as you want to give it. Most owners come back to it a few times over several days. The more you put in, the more specific the plan that comes out. There is no rush and no clock.'],
  ['What happens after I get my Blueprint?', 'You will see your implementation options right after checkout, including a free upgrade that covers both the do-it-yourself path and the deploy-it-for-me path. Nothing extra to figure out, nothing hidden.'],
  ['What if it does not deliver for me?', 'We would rather make it right than send you away. If your first 14 days do not deliver the clarity we promised, tell us — we will extend access, set up a working session, or help you build what you came for.'],
  ['Do I need to be technical?', 'No. If you can have a conversation about your business, you can use RoleForge.'],
  ['Does this replace my team?', 'No. It removes the work your team should not be doing in the first place — the coordination, follow-up, and admin that fills their days.'],
];

/** Stylized deliverable preview — three role cards from a finished Blueprint. */
function BlueprintArtifact() {
  const cards = [
    {
      no: 'JOB 01',
      name: 'Office manager',
      line: 'Watches inbox, calendar, projects. Escalates only what needs you.',
      skills: ['Inbox triage', 'Daily brief', 'Task routing'],
      classes: 'z-30 -rotate-1',
      i: 3,
    },
    {
      no: 'JOB 02',
      name: 'Sales follow-up',
      line: 'Every lead followed up. Every call booked. CRM always current.',
      skills: ['Lead follow-up', 'Call booking'],
      classes: 'z-20 ml-6 -mt-3 rotate-[0.6deg]',
      i: 4,
    },
    {
      no: 'JOB 03',
      name: 'Reports analyst',
      line: 'Your numbers, summarized and delivered every Monday morning.',
      skills: ['Weekly report'],
      classes: 'z-10 ml-12 -mt-3 rotate-[1.4deg]',
      i: 5,
    },
  ];
  return (
    <div aria-hidden="true" className="relative hidden select-none lg:block">
      {cards.map((c) => (
        <div
          key={c.no}
          style={{ '--fv2-i': c.i } as CSSProperties}
          className={`fv2-reveal relative w-[340px] rounded-lg border border-white/12 bg-[#0d1219]/95 p-5 shadow-[0_24px_60px_rgba(0,0,0,0.55)] backdrop-blur transition hover:-translate-y-1 ${c.classes}`}
        >
          <div className="flex items-center justify-between">
            <span className="font-mono text-[10px] tracking-[0.24em] text-brand-teal/80">{c.no}</span>
            <span className="h-1.5 w-1.5 rounded-full bg-brand-coral/80" />
          </div>
          <p className="mt-2.5 font-display text-lg font-semibold tracking-tight text-white">{c.name}</p>
          <p className="mt-1.5 text-xs leading-5 text-slate-400">{c.line}</p>
          <div className="mt-3.5 flex flex-wrap gap-1.5">
            {c.skills.map((s) => (
              <span key={s} className="rounded border border-white/10 bg-white/[0.04] px-2 py-0.5 font-mono text-[10px] text-slate-400">
                {s}
              </span>
            ))}
          </div>
        </div>
      ))}
      <p
        style={{ '--fv2-i': 6 } as CSSProperties}
        className="fv2-reveal ml-12 mt-5 font-mono text-[10px] uppercase tracking-[0.24em] text-slate-600"
      >
        Three jobs, one AI employee — from a finished Blueprint
      </p>
    </div>
  );
}

function StickyCta({ priceCents }: { priceCents: number }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 640);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-[#080a0f]/92 px-4 py-3 backdrop-blur-xl transition duration-300 ${
        visible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      }`}
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="font-display text-sm font-semibold text-white">AI Workforce Blueprint</p>
          <p className="font-mono text-[11px] text-slate-500">{formatUsd(priceCents)} · one time · no subscription</p>
        </div>
        <PrimaryCtaLink to="/checkout" className="w-full px-6 py-3.5 sm:w-auto">
          {ctaLabel(priceCents)}
        </PrimaryCtaLink>
      </div>
    </div>
  );
}

export function BlueprintSalesPage() {
  const prices = useFunnelPriceCatalog();
  const blueprintPrice = formatUsd(prices.blueprintCents);

  return (
    <>
      {/* Slim funnel header — logo + the one action. */}
      <div className="border-b border-white/[0.07] px-5 md:px-8">
        <div className="mx-auto flex max-w-5xl items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <img src="/roleforge-icon.svg" alt="" className="h-7 w-7 rounded-md" />
            <span className="font-display text-base font-semibold tracking-tight text-white">RoleForge</span>
          </div>
          <div className="flex items-center gap-2 font-mono text-[11px] text-slate-500">
            <Lock className="h-3.5 w-3.5 text-brand-teal" />
            Secure Stripe checkout
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden px-5 pb-16 pt-16 md:px-8 md:pb-24 md:pt-24">
        <BlueprintGrid />
        <div className="relative mx-auto grid max-w-5xl gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
          <div>
            <Kicker className="fv2-reveal !text-brand-teal/90" >RoleForge — AI Workforce Blueprint</Kicker>
            <h1
              style={{ '--fv2-i': 1 } as CSSProperties}
              className="fv2-reveal mt-5 font-display text-[44px] font-semibold leading-[1.02] tracking-tight text-white md:text-[64px]"
            >
              You are still the hardest-working employee in the building.
            </h1>
            <p
              style={{ '--fv2-i': 2 } as CSSProperties}
              className="fv2-reveal mt-6 max-w-xl text-lg leading-8 text-slate-300"
            >
              RoleForge maps the jobs an AI employee can genuinely take off your
              plate — then hands you a written, deployable plan to put her to work. One guided
              conversation. One Blueprint. <span className="font-semibold text-white">{blueprintPrice}, one time.</span>
            </p>
            <FunnelPriceNotice snapshot={prices} />
            <div style={{ '--fv2-i': 3 } as CSSProperties} className="fv2-reveal mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
              <PrimaryCtaLink to="/checkout">{ctaLabel(prices.blueprintCents)}</PrimaryCtaLink>
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <ShieldCheck className="h-4 w-4 text-brand-teal" />
                14-day Value Promise
              </div>
            </div>
          </div>
          <BlueprintArtifact />
        </div>
      </section>

      <Section>
        <SectionMark no="01" label="The bottleneck" />
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr]">
          <DisplayHeading>You did not start this business to be its top employee.</DisplayHeading>
          <div className="space-y-5 text-base leading-8 text-slate-300">
            <p>
              You started it to build something. Instead you are answering customer emails at
              11pm, following up with leads nobody else will touch, and writing reports on
              Sundays because that is the only quiet time you have.
            </p>
            <p className="text-xl font-semibold text-white">
              The business grew. Your time did not.
            </p>
            <p>Every fix you have been sold changes what is on your desk without moving work off it:</p>
            <div className="grid gap-3 pt-1">
              {costOptions.map(([title, body]) => (
                <div key={title} className="rounded-md border border-white/10 bg-white/[0.03] p-4">
                  <strong className="text-white">{title}</strong>{' '}
                  <span className="text-slate-400">{body}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      <Section className="bg-[#0b0f16]/60">
        <SectionMark no="02" label="What changes" />
        <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr]">
          <div>
            <DisplayHeading>What if one AI employee actually did the work?</DisplayHeading>
            <p className="mt-6 text-lg leading-8 text-slate-300">
              Not an advisor. Not a chat box that helps you draft while you do the writing. One employee who does it.
            </p>
          </div>
          <div className="grid gap-3">
            {jobs.map(([title, body]) => (
              <div key={title} className="flex gap-4 rounded-md border border-white/10 bg-[#101720] p-4">
                <BriefcaseBusiness className="mt-1 h-5 w-5 shrink-0 text-brand-coral/90" />
                <p className="text-slate-300">
                  <strong className="text-white">{title}:</strong> {body}
                </p>
              </div>
            ))}
          </div>
        </div>
        <p className="mt-10 max-w-3xl text-lg leading-8 text-slate-300">
          This is what AI can finally do. The catch: until now you needed a developer or a
          $20,000 engagement to figure out which jobs to hand her and how to make them fit
          your business. <span className="font-semibold text-white">That gap is what RoleForge closes.</span>
        </p>
      </Section>

      <Section>
        <SectionMark no="03" label={`What ${blueprintPrice} buys`} />
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <DisplayHeading>A real conversation. A written plan. Specific to your business.</DisplayHeading>
            <p className="mt-6 text-lg leading-8 text-slate-300">
              You sit down with an AI consultant that interviews you about how your business
              actually runs. Not a quiz. Not a form. It goes as deep as you want to take it.
            </p>
            <p className="mt-4 text-lg leading-8 text-slate-300">
              What you will not get is a 40-page PDF that sits in your downloads folder.
            </p>
          </div>
          <div className="space-y-4">
            {[
              ['The jobs your AI employee should hold.', 'Each job, named and described: what she is responsible for, how autonomous she should be, the judgment she is trusted with.'],
              ['The skills each job uses.', 'What she reads, produces, and plugs into for each job — mapped to the work you already do.'],
              ['The order to set them up in.', 'Which job buys back the most of your time first, based on what is eating it today. A short, prioritized plan — not twenty ideas you will never touch.'],
            ].map(([title, body], i) => (
              <div key={title} className="rounded-md border border-white/10 bg-white/[0.03] p-5">
                <span className="font-mono text-xs font-semibold text-brand-teal/80">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="mt-2 font-display text-lg font-semibold text-white">{title}</h3>
                <p className="mt-2 text-sm leading-7 text-slate-400">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section className="bg-[#0b0f16]/60">
        <SectionMark no="04" label="Who this fits" />
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-lg border border-brand-teal/20 bg-brand-teal/[0.07] p-6">
            <h2 className="font-display text-xl font-semibold text-white">This is for you if</h2>
            <ul className="mt-6 space-y-4 text-slate-300">
              {[
                'You run a service business between $1M and $10M in revenue',
                'You are the bottleneck — most work routes through you',
                'AI tools have helped a little, but never enough',
                'You are done buying software that does not move work off your desk',
              ].map((item) => (
                <li key={item} className="flex gap-3">
                  <Check className="mt-1 h-5 w-5 shrink-0 text-brand-teal" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg border border-white/10 bg-white/[0.03] p-6">
            <h2 className="font-display text-xl font-semibold text-white">This is not for you if</h2>
            <ul className="mt-6 space-y-4 text-slate-400">
              {[
                'You want an AI chatbot for your website',
                'You are a developer who wants to build agent workflows yourself',
                'You are an enterprise buyer expecting a procurement cycle',
                'You are pre-revenue and looking for free tools to start',
              ].map((item) => (
                <li key={item} className="flex gap-3">
                  <X className="mt-1 h-5 w-5 shrink-0 text-brand-coral/80" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {audience.slice(0, 3).map(([title, body]) => (
            <div key={title} className="rounded-md border border-white/10 bg-white/[0.03] p-5">
              <h3 className="font-semibold text-white">{title}</h3>
              <p className="mt-2 text-sm leading-7 text-slate-400">{body}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section>
        <SectionMark no="05" label="The obvious question" />
        <DisplayHeading>Why not just use ChatGPT?</DisplayHeading>
        <div className="mt-10 overflow-hidden rounded-lg border border-white/10">
          {comparisons.map(([from, body]) => (
            <div key={from} className="grid gap-2 border-b border-white/10 bg-[#0e141d] p-5 last:border-b-0 md:grid-cols-[180px_1fr]">
              <p className="font-mono text-sm font-semibold text-slate-500">{from}</p>
              <p className="text-sm leading-7 text-slate-300">{body}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 max-w-3xl text-lg leading-8 text-slate-300">
          RoleForge tells you which jobs your AI employee should take on — then shows you how
          to put her to work.
        </p>
      </Section>

      <Section className="bg-[#0b0f16]/60">
        <SectionMark no="06" label="Who built this" />
        <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
          <DisplayHeading>Built by someone who has cleaned up after small-business software for 18 years.</DisplayHeading>
          <div className="space-y-5 text-base leading-8 text-slate-300">
            <p>I am Dustin. I built RoleForge.</p>
            <p>
              I have run an IT services company for small businesses since 2008 — the kind that
              gets called when something breaks and someone has to fix it. I have watched owners
              get sold a hundred tools that promised to change their business and delivered
              another monthly fee.
            </p>
            <p>
              Most AI tools rolling out right now are the same story: a chat box, some prompts,
              and the owner still doing the work. RoleForge is what I wish I could have handed my
              own clients for the last decade.
            </p>
          </div>
        </div>
      </Section>

      <Section id="offer">
        <SectionMark no="07" label="The offer" />
        <div className="grid gap-10 lg:grid-cols-[1fr_420px] lg:items-start">
          <div>
            <DisplayHeading>One conversation between you and a working AI plan.</DisplayHeading>
            <div className="mt-8 space-y-4">
              {[
                'A guided conversation with an AI consultant that learns how your business operates and where the time actually goes.',
                'Your AI Workforce Blueprint — every job mapped: what you keep doing, what she takes over, and the skills to build it. Written, specific, yours to keep — and it works with the AI tools you already use.',
                'Clear implementation options when the Blueprint is done — including a path where it deploys itself.',
              ].map((item) => (
                <div key={item} className="flex gap-3 text-base leading-8 text-slate-300">
                  <Check className="mt-1.5 h-5 w-5 shrink-0 text-brand-teal" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <ValuePromise className="mt-8 max-w-xl" />
          </div>
          <LedgerCard>
            <Kicker>Today</Kicker>
            <div className="mt-3">
              <LedgerRow
                label="AI Workforce Blueprint"
                sub="Guided sessions plus your written, deployable plan"
                amount={blueprintPrice}
                accent
              />
              <LedgerRow label="Subscriptions on this page" sub="None. One-time payment." amount="$0" muted />
            </div>
            <div className="mt-5">
              <PrimaryCtaLink to="/checkout" className="w-full">
                {ctaLabel(prices.blueprintCents)}
              </PrimaryCtaLink>
            </div>
            <p className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-500">
              <Lock className="h-3.5 w-3.5 text-brand-teal" />
              Card details collected by Stripe. We never see them.
            </p>
          </LedgerCard>
        </div>
      </Section>

      <Section className="bg-[#0b0f16]/60">
        <SectionMark no="08" label="Asked before buying" />
        <div className="divide-y divide-white/10 rounded-lg border border-white/10">
          {faqs.map(([q, a]) => (
            <details key={q} className="group bg-white/[0.02] p-5 open:bg-white/[0.045]">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-semibold text-white">
                {q}
                <span className="font-mono text-brand-coral transition group-open:rotate-45">+</span>
              </summary>
              <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-400">{a}</p>
            </details>
          ))}
        </div>
      </Section>

      <Section>
        <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <DisplayHeading>Two ways to leave this page.</DisplayHeading>
          <div className="space-y-5 text-base leading-8 text-slate-300">
            <p>
              <strong className="text-white">Close the tab.</strong> Go back to the inbox. Write
              yourself a note that says look into AI for the business someday. Find the note
              again next quarter.
            </p>
            <p>
              <strong className="text-white">Or spend {blueprintPrice}.</strong> Find out exactly what an AI
              workforce would do in your business, and walk away with a plan you can act on
              either way.
            </p>
            <div className="pt-2">
              <PrimaryCtaLink to="/checkout">{ctaLabel(prices.blueprintCents)}</PrimaryCtaLink>
            </div>
          </div>
        </div>
      </Section>

      <StickyCta priceCents={prices.blueprintCents} />
    </>
  );
}
